import { useState, useEffect } from 'react';

const NAV_LINKS = [
  { label: '홈', id: 'home' },
  { label: '이안미디어 소개', id: 'ian-media' },
  { label: '광고비 낭비 진단', id: 'diagnostic' },
  { label: '퍼포먼스 마케팅', id: 'performance-system' },
  { label: '성과데이터', id: 'results' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeId, setActiveId] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);

      const sections = ['home', 'ian-media', 'diagnostic', 'performance-system', 'results', 'contact'];
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && window.scrollY + 120 >= el.offsetTop) {
          setActiveId(sections[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    if (id === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <nav
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'top-0 bg-[#07091A]/95 backdrop-blur-xl border-b border-white/6'
            : 'top-[56px] bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-14 md:h-16">

            {/* Logo */}
            <button
              onClick={() => scrollTo('home')}
              className="flex items-center gap-2 cursor-pointer flex-shrink-0 group"
            >
              <img
                src="https://static.readdy.ai/image/082ecbf3b95a41c4fe2e0b7873d9af01/65542f73881bfda9fc8d299d14161080.png"
                alt="IAN MEDIA"
                className="h-9 md:h-11 w-auto"
              />
              <span
                className="text-sm md:text-base font-black text-white tracking-tight group-hover:text-[#00FF87] transition-colors duration-200"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                IAN MEDIA
              </span>
            </button>

            {/* Desktop Nav Links */}
            <div className="hidden lg:flex items-center gap-0">
              {NAV_LINKS.map((link) => {
                const isActive = activeId === link.id;
                return (
                  <button
                    key={link.id}
                    onClick={() => scrollTo(link.id)}
                    className={`relative px-2.5 py-2 text-[13px] font-medium transition-all duration-200 rounded-lg cursor-pointer whitespace-nowrap group ${
                      isActive ? 'text-white' : 'text-white/45 hover:text-white/80'
                    }`}
                  >
                    {isActive && (
                      <span className="absolute inset-0 bg-white/5 rounded-lg" />
                    )}
                    <span className="relative">{link.label}</span>
                    {isActive && (
                      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-[#00FF87] rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Desktop CTA + Mobile Hamburger */}
            <div className="flex items-center gap-2.5">
              {/* CTA Button */}
              <button
                onClick={() => scrollTo('contact')}
                className="hidden sm:flex items-center gap-1.5 cursor-pointer whitespace-nowrap group relative overflow-hidden rounded-lg transition-all duration-200"
                style={{
                  background: 'linear-gradient(135deg, #00FF87 0%, #00e87a 100%)',
                  padding: '8px 18px',
                  boxShadow: '0 0 20px rgba(0,255,135,0.22)',
                }}
              >
                <span className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors duration-200 rounded-lg" />
                <i className="ri-file-list-3-line relative text-black text-sm" />
                <span className="relative text-black font-black text-xs tracking-tight">무료 진단 신청</span>
                <i className="ri-arrow-right-line relative text-black text-xs group-hover:translate-x-0.5 transition-transform duration-200"></i>
              </button>

              {/* Hamburger for mobile */}
              <button
                onClick={() => setMobileOpen(p => !p)}
                className="lg:hidden w-9 h-9 flex flex-col items-center justify-center gap-1.5 cursor-pointer rounded-lg hover:bg-white/5 transition-colors"
                aria-label="메뉴 열기"
              >
                <span
                  className={`block w-5 h-0.5 bg-white/70 rounded-full transition-all duration-200 ${mobileOpen ? 'translate-y-2 rotate-45' : ''}`}
                />
                <span
                  className={`block w-5 h-0.5 bg-white/70 rounded-full transition-all duration-200 ${mobileOpen ? 'opacity-0 scale-x-0' : ''}`}
                />
                <span
                  className={`block w-5 h-0.5 bg-white/70 rounded-full transition-all duration-200 ${mobileOpen ? '-translate-y-2 -rotate-45' : ''}`}
                />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ background: 'rgba(7,9,26,0.98)', backdropFilter: 'blur(20px)' }}
      >
        {/* Close area top */}
        <div className="h-[89px]" />

        <div className="px-5 pt-4 pb-8 flex flex-col gap-1">
          {NAV_LINKS.map((link, i) => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              className="flex items-center justify-between w-full px-4 py-4 rounded-xl text-left cursor-pointer transition-all duration-200 hover:bg-white/5 group"
              style={{
                opacity: mobileOpen ? 1 : 0,
                transform: mobileOpen ? 'translateY(0)' : 'translateY(12px)',
                transitionDelay: `${i * 40}ms`,
              }}
            >
              <span
                className={`text-base font-semibold transition-colors ${
                  activeId === link.id ? 'text-[#00FF87]' : 'text-white/70 group-hover:text-white'
                }`}
              >
                {link.label}
              </span>
              {activeId === link.id && (
                <span className="w-1.5 h-1.5 bg-[#00FF87] rounded-full flex-shrink-0" />
              )}
            </button>
          ))}

          {/* Mobile CTA */}
          <div
            className="mt-6 px-1"
            style={{
              opacity: mobileOpen ? 1 : 0,
              transform: mobileOpen ? 'translateY(0)' : 'translateY(12px)',
              transitionDelay: `${NAV_LINKS.length * 40 + 40}ms`,
              transition: 'all 0.3s ease',
            }}
          >
            <button
              onClick={() => scrollTo('contact')}
              className="w-full flex items-center justify-center gap-2.5 bg-[#00FF87] text-black font-black py-4 rounded-xl cursor-pointer text-base transition-all duration-200 hover:bg-[#00e87a]"
              style={{ boxShadow: '0 0 40px rgba(0,255,135,0.25)' }}
            >
              지금 무료 진단 신청하기
              <i className="ri-arrow-right-line text-lg"></i>
            </button>
            <p className="text-center text-white/25 text-xs mt-3">상담 비용 0원 · 계약 강요 없음</p>
          </div>
        </div>
      </div>
    </>
  );
}
